import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { TablesInsert, Json } from '@/integrations/supabase/types';

export type ProposalType = 'field' | 'enum_value' | 'standard' | 'adf_element' | 'adf_mapping' | 'adf_attribute';

export interface StandardProposal {
  id: string;
  proposal_type: ProposalType;
  title: string;
  description: string;
  justification: string;
  proposed_field_name: string | null;
  proposed_field_type: string | null;
  proposed_field_format: string | null;
  proposed_field_required: boolean | null;
  proposed_enum_value: string | null;
  target_field_name: string | null;
  target_standard_id: string | null;
  target_adf_element_id: string | null;
  target_category: string | null;
  example_json: Json | null;
  submitter_name: string;
  submitter_email: string;
  submitter_organization: string | null;
  user_id: string | null;
  status: 'pending' | 'under_review' | 'approved' | 'rejected' | 'implemented';
  admin_notes: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  vote_count: number;
  created_at: string;
  updated_at: string;
}

export interface ProposalComment {
  id: string;
  proposal_id: string;
  user_id: string | null;
  commenter_name: string;
  commenter_email: string;
  is_admin: boolean;
  content: string;
  created_at: string;
  updated_at: string;
}

export interface ProposalVote {
  id: string;
  proposal_id: string;
  user_id: string | null;
  session_id: string | null;
  created_at: string;
}

interface ProposalFilters {
  type?: string;
  status?: string;
  sortBy?: 'votes' | 'date';
  isAdmin?: boolean; // Use base table for admin access
}

// Get or create session ID for anonymous voting
function getSessionId(): string {
  const key = 'proposal_session_id';
  let sessionId = localStorage.getItem(key);
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    localStorage.setItem(key, sessionId);
  }
  return sessionId;
}

export function useProposals(filters?: ProposalFilters) {
  return useQuery({
    queryKey: ['proposals', filters],
    staleTime: 1 * 60 * 1000, // 1 minute - user-generated content changes more often
    queryFn: async () => {
      // Admins query base table for full access including emails; public uses view
      const tableName = filters?.isAdmin ? 'standard_proposals' : 'standard_proposals_public';
      let query = supabase
        .from(tableName as any)
        .select('*');

      if (filters?.type && filters.type !== 'all') {
        query = query.eq('proposal_type', filters.type);
      }

      if (filters?.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }

      if (filters?.sortBy === 'votes') {
        query = query.order('vote_count', { ascending: false });
      } else {
        query = query.order('created_at', { ascending: false });
      }

      const { data, error } = await query;

      if (error) throw error;
      
      // If admin, return full data; otherwise map to hide emails
      if (filters?.isAdmin) {
        return (data || []) as unknown as StandardProposal[];
      }
      
      // Map to expected interface - email is excluded from view
      const proposals = (data as unknown) as Record<string, unknown>[] | null;
      return (proposals || []).map(proposal => ({
        ...proposal,
        submitter_email: '', // Email not available from public view
        user_id: null, // user_id not in public view
        reviewed_by: null, // reviewed_by not in public view
      })) as StandardProposal[];
    },
  });
}

export function useProposal(id: string) {
  return useQuery({
    queryKey: ['proposal', id],
    queryFn: async () => {
      // Use public view that excludes email for security
      const { data, error } = await supabase
        .from('standard_proposals_public' as any)
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      const proposal = (data as unknown) as Record<string, unknown>;
      return {
        ...proposal,
        submitter_email: '', // Email not available from public view
        user_id: null, // user_id not in public view
        reviewed_by: null, // reviewed_by not in public view
      } as StandardProposal;
    },
    enabled: !!id,
  });
}

export function useProposalComments(proposalId: string, isAdmin?: boolean) {
  return useQuery({
    queryKey: ['proposal-comments', proposalId, isAdmin],
    queryFn: async () => {
      // Admins query base table for full access; public uses view
      const tableName = isAdmin ? 'proposal_comments' : 'proposal_comments_public';
      const { data, error } = await supabase
        .from(tableName as any)
        .select('*')
        .eq('proposal_id', proposalId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      
      // If admin, return full data; otherwise map to hide emails
      if (isAdmin) {
        return (data || []) as unknown as ProposalComment[];
      }
      
      // Map to expected interface - email is excluded from view
      const comments = (data as unknown) as Record<string, unknown>[] | null;
      return (comments || []).map(comment => ({
        ...comment,
        commenter_email: '', // Email not available from public view
        user_id: null, // user_id not in public view
      })) as ProposalComment[];
    },
    enabled: !!proposalId,
  });
}

export function useProposalVotes(proposalId: string) {
  const { user } = useAuth();
  const sessionId = getSessionId();

  return useQuery({
    queryKey: ['proposal-votes', proposalId, user?.id, sessionId],
    queryFn: async () => {
      // Check if current user/session has voted
      let query = supabase
        .from('proposal_votes')
        .select('id')
        .eq('proposal_id', proposalId);

      if (user?.id) {
        query = query.eq('user_id', user.id);
      } else {
        query = query.eq('session_id', sessionId);
      }

      const { data, error } = await query;
      if (error) throw error;

      return {
        hasVoted: data && data.length > 0,
        voteId: data?.[0]?.id,
      };
    },
    enabled: !!proposalId,
  });
}

export function useHasVoted(proposalId: string) {
  const { data } = useProposalVotes(proposalId);
  return data?.hasVoted ?? false;
}

interface SubmitProposalInput {
  proposal_type: ProposalType;
  title: string;
  description: string;
  justification: string;
  proposed_field_name?: string;
  proposed_field_type?: string;
  proposed_field_format?: string;
  proposed_field_required?: boolean;
  proposed_enum_value?: string;
  target_field_name?: string;
  target_standard_id?: string;
  target_adf_element_id?: string;
  target_category?: string;
  example_json?: Json;
  submitter_name: string;
  submitter_email: string;
  submitter_organization?: string;
}

export function useSubmitProposal() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (input: SubmitProposalInput) => {
      const insertData: TablesInsert<'standard_proposals'> = {
        proposal_type: input.proposal_type,
        title: input.title,
        description: input.description,
        justification: input.justification,
        submitter_name: input.submitter_name,
        submitter_email: input.submitter_email,
        submitter_organization: input.submitter_organization || null,
        proposed_field_name: input.proposed_field_name || null,
        proposed_field_type: input.proposed_field_type || null,
        proposed_field_format: input.proposed_field_format || null,
        proposed_field_required: input.proposed_field_required ?? false,
        proposed_enum_value: input.proposed_enum_value || null,
        target_field_name: input.target_field_name || null,
        target_standard_id: input.target_standard_id || null,
        target_adf_element_id: input.target_adf_element_id || null,
        target_category: input.target_category || null,
        example_json: input.example_json || null,
      };

      const { data, error } = await supabase
        .from('standard_proposals')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proposals'] });
    },
  });
}

export function useVoteProposal() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ proposalId, action }: { proposalId: string; action: 'vote' | 'unvote' }) => {
      const sessionId = getSessionId();

      if (action === 'vote') {
        const insertData: { proposal_id: string; user_id?: string; session_id?: string } = {
          proposal_id: proposalId,
        };

        if (user?.id) {
          insertData.user_id = user.id;
        } else {
          insertData.session_id = sessionId;
        }

        const { error } = await supabase
          .from('proposal_votes')
          .insert(insertData);

        if (error) throw error;
      } else {
        let query = supabase
          .from('proposal_votes')
          .delete()
          .eq('proposal_id', proposalId);

        if (user?.id) {
          query = query.eq('user_id', user.id);
        } else {
          query = query.eq('session_id', sessionId);
        }

        const { error } = await query;
        if (error) throw error;
      }
    },
    onSuccess: (_, { proposalId }) => {
      queryClient.invalidateQueries({ queryKey: ['proposals'] });
      queryClient.invalidateQueries({ queryKey: ['proposal', proposalId] });
      queryClient.invalidateQueries({ queryKey: ['proposal-votes', proposalId] });
    },
  });
}

interface AddCommentInput {
  proposal_id: string;
  content: string;
  commenter_name: string;
  commenter_email: string;
}

export function useAddComment() {
  const queryClient = useQueryClient();
  const { user, isAdmin } = useAuth();

  return useMutation({
    mutationFn: async (input: AddCommentInput) => {
      const { data, error } = await supabase
        .from('proposal_comments')
        .insert({
          ...input,
          user_id: user?.id || null,
          is_admin: isAdmin,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, { proposal_id }) => {
      queryClient.invalidateQueries({ queryKey: ['proposal-comments', proposal_id] });
    },
  });
}

export function useUpdateProposalStatus() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ 
      proposalId, 
      status, 
      admin_notes 
    }: { 
      proposalId: string; 
      status: StandardProposal['status']; 
      admin_notes?: string;
    }) => {
      const { data, error } = await supabase
        .from('standard_proposals')
        .update({
          status,
          admin_notes,
          reviewed_by: user?.id,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', proposalId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, { proposalId }) => {
      queryClient.invalidateQueries({ queryKey: ['proposals'] });
      queryClient.invalidateQueries({ queryKey: ['proposal', proposalId] });
    },
  });
}

export function useDeleteProposal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (proposalId: string) => {
      const { error } = await supabase
        .from('standard_proposals')
        .delete()
        .eq('id', proposalId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proposals'] });
    },
  });
}

// Hook to get comment count for a proposal
export function useProposalCommentCount(proposalId: string) {
  return useQuery({
    queryKey: ['proposal-comment-count', proposalId],
    queryFn: async () => {
      // Use public view for counting
      const { count, error } = await supabase
        .from('proposal_comments_public' as any)
        .select('*', { count: 'exact', head: true })
        .eq('proposal_id', proposalId);

      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!proposalId,
  });
}

// Hook to get all comment counts for multiple proposals at once
export function useProposalCommentCounts(proposalIds: string[]) {
  return useQuery({
    queryKey: ['proposal-comment-counts', proposalIds],
    queryFn: async () => {
      if (proposalIds.length === 0) return {};

      // Use public view for counting
      const { data, error } = await supabase
        .from('proposal_comments_public' as any)
        .select('proposal_id')
        .in('proposal_id', proposalIds);

      if (error) throw error;

      const counts: Record<string, number> = {};
      proposalIds.forEach(id => counts[id] = 0);
      const comments = (data as unknown) as { proposal_id: string }[] | null;
      comments?.forEach((comment) => {
        counts[comment.proposal_id] = (counts[comment.proposal_id] || 0) + 1;
      });

      return counts;
    },
    enabled: proposalIds.length > 0,
  });
}
